import { createApi } from '@reduxjs/toolkit/query/react';
import { endpoints } from 'config/api';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { CompanyUser, TransformedUser, RoleRef } from './types';
import { companyApi } from '../company/companyApi';

// Helper to get role name from roleRef or fallback to legacy role
const getRoleName = (user: CompanyUser): string => {
  if (user.roleRef && typeof user.roleRef === 'object') {
    return (user.roleRef as RoleRef).name;
  }
  return user.role;
};

// Helper to get role color from roleRef
const getRoleColor = (user: CompanyUser): string | undefined => {
  if (user.roleRef && typeof user.roleRef === 'object') {
    return (user.roleRef as RoleRef).color;
  }
  return undefined;
};

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Users'],
  endpoints: (builder) => ({
    getCompanyUsers: builder.query<TransformedUser[], void>({
      query: () => ({
        url: endpoints.users.company,
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.map((user: CompanyUser) => {
          const roleName = getRoleName(user);
          const roleColor = getRoleColor(user);
          return {
            value: user._id,
            label: `${user.firstName} ${user.lastName}`,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.role,
            roleRef: user.roleRef,
            roleName,
            roleColor,
            phone: user.phone,
            isContactPerson: user.isContactPerson,
            id: user._id,
          };
        });
      },
      providesTags: ['Users'],
    }),

    createUser: builder.mutation({
      query: (userData) => ({
        url: endpoints.users.base,
        method: 'POST',
        data: userData,
      }),
      invalidatesTags: ['Users'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate company seats data when user is created
          dispatch(companyApi.util.invalidateTags(['Company']));
        } catch {}
      },
    }),

    updateUser: builder.mutation({
      query: ({ userId, userData }) => ({
        url: endpoints.users.asAdmin(userId),
        method: 'PUT',
        data: userData,
      }),
      invalidatesTags: ['Users'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate company seats data when user is updated
          dispatch(companyApi.util.invalidateTags(['Company']));
        } catch {}
      },
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `${endpoints.users.base}/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate company seats data when user is deleted
          dispatch(companyApi.util.invalidateTags(['Company']));
        } catch {}
      },
    }),

    searchUsers: builder.query({
      query: (query) => ({
        url: endpoints.users.search(query),
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetCompanyUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useSearchUsersQuery,
  useLazySearchUsersQuery,
} = usersApi;
